import React, { useContext } from 'react';
import { Bar } from 'react-chartjs-2';
import { Chart as ChartJS, CategoryScale, LinearScale, BarElement, Title, Tooltip, Legend, SubTitle } from 'chart.js';
import Context from '../../../../utils/context';

ChartJS.register(CategoryScale, LinearScale, BarElement, Title, Tooltip, Legend, SubTitle);

const StackedBarChart = ({ title, yTitle, xTitle, data }) => {
  const context = useContext(Context);
  const isMobile = context.viewport.getIsMobile();

  if (!data) {
    return <></>;
  }

  const colors = [
    '#59b6be',
    '#578ec5',
    '#7974b4',
    '#935791',
    '#9c3d62',
    '#69d9b0',
    '#324b4d',
    '#94b0b3',
    '#d393d2',
    '#c5a45b',
    '#0e6855',
    '#00a9be',
  ];

  const patientCount = data.total;
  // Purposefully manipulating data to remove categories that have all no data
  // Might need to one day remove this portion of code if we build specific charts for each client
  const results = Object.keys(data.results).reduce((filteredResults, questionLabel) => {
    const questionData = data.results[questionLabel];

    // Check if all data for this question is in "no data" categories
    const totalResponses = Object.values(questionData).reduce((sum, count) => sum + count, 0);
    const noDataResponses = Object.keys(questionData)
      .filter((category) => category.toLowerCase() === 'no data')
      .reduce((sum, category) => sum + questionData[category], 0);

    // Only include questions that have some data outside of "no data" categories
    if (totalResponses > 0 && noDataResponses < totalResponses) {
      filteredResults[questionLabel] = questionData;
    }

    return filteredResults;
  }, {});

  let labels = [];
  let nonTrunctedLabels = [];
  const categorySet = new Set();

  // Collect labels and all response categories dynamically
  Object.keys(results).forEach((questionLabel) => {
    const truncatedLabel = isMobile
      ? questionLabel.length > 15
        ? questionLabel.substring(0, 15) + '...'
        : questionLabel
      : questionLabel.length > 35
        ? questionLabel.substring(0, 35) + '...'
        : questionLabel;

    labels.push(truncatedLabel);
    nonTrunctedLabels.push(questionLabel);
    Object.keys(results[questionLabel]).forEach((responseKey) => {
      categorySet.add(responseKey);
    });
  });

  const categories = Array.from(categorySet);
  const datasets = categories.map((category, index) => {
    const backgroundColor = colors[index % colors.length];
    const dataValues = Object.keys(results).map((questionLabel) => {
      return results[questionLabel][category] || 0;
    });

    return {
      label: category,
      data: dataValues,
      backgroundColor,
      stack: 'Stack 0',
      hidden: category.toLowerCase() === 'no data', // Hide "no data" categories by default
    };
  });

  const chartData = {
    labels,
    nonTrunctedLabels,
    datasets,
  };

  const options = {
    indexAxis: 'y',
    elements: {
      bar: {
        borderWidth: 2,
      },
    },
    responsive: true,
    plugins: {
      legend: {
        display: true,
        position: 'right',
        onClick: (e, legendItem, legend) => {
          const index = legendItem.datasetIndex;
          const chart = legend.chart;
          const meta = chart.getDatasetMeta(index);

          // Toggle visibility
          meta.hidden = meta.hidden === null ? !chart.data.datasets[index].hidden : null;
          chart.update();
        },
      },
      title: {
        display: true,
        text: title,
        font: {
          size: isMobile ? 10 : 12,
          family: 'Arial',
          color: '#666666',
        },
      },
      subtitle: {
        display: true,
        text: `${patientCount} total mentees`,
        font: {
          size: isMobile ? 10 : 12,
          family: 'Arial',
          color: '#666666',
        },
      },
      tooltip: {
        callbacks: {
          title: (context) => {
            return nonTrunctedLabels[context[0].dataIndex];
          },
          label: (context) => {
            return ` ${context.formattedValue} patients`;
          },
        },
        font: {
          size: isMobile ? 10 : 12,
          family: 'Arial',
          color: '#666666',
        },
      },
    },
    scales: {
      y: {
        stacked: true,
        title: {
          display: true,
          text: yTitle,
          padding: 0,
          color: '#666666',
          font: {
            size: isMobile ? 10 : 12,
            family: 'Arial',
          },
        },
        ticks: {
          font: {
            size: isMobile ? 10 : 12,
          },
          callback: function (_, index) {
            if (isMobile) {
              const label = nonTrunctedLabels[index];
              return label.length > 10 ? label.substring(0, 10) + '...' : label;
            }
            // For desktop, wrap text after 35 characters
            const label = nonTrunctedLabels[index];
            if (label.length > 35) {
              const words = label.split(' ');
              let lines = [];
              let currentLine = '';

              words.forEach((word) => {
                if ((currentLine + word).length > 35) {
                  lines.push(currentLine.trim());
                  currentLine = word + ' ';
                } else {
                  currentLine += word + ' ';
                }
              });
              if (currentLine) {
                lines.push(currentLine.trim());
              }
              return lines;
            }
            return label;
          },
          maxRotation: 0,
          minRotation: 0,
          padding: 10,
        },
      },
      x: {
        stacked: true,
        title: {
          display: true,
          text: xTitle,
          color: '#666666',
          font: {
            size: isMobile ? 10 : 12,
            family: 'Arial',
          },
        },
        ticks: {
          precision: 0,
        },
      },
    },
  };

  return <Bar options={options} data={chartData} />;
};

export default StackedBarChart;
